function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function lerp(a, b, t) {
  return a + (b - a) * clamp(t, 0, 1);
}

function easeOutQuad(t) {
  const x = clamp(t, 0, 1);
  return 1 - (1 - x) * (1 - x);
}

function easeOutBack(t) {
  const x = clamp(t, 0, 1);
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2);
}

function angleDiff(a, b) {
  return Math.atan2(Math.sin(a - b), Math.cos(a - b));
}

function lerpAngle(a, b, t) {
  const x = clamp(t, 0, 1);
  return a + angleDiff(b, a) * x;
}

function numberOr(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function cloneTeamScores(scores, fallback = null) {
  const src = scores && typeof scores === 'object' ? scores : null;
  const base = fallback && typeof fallback === 'object' ? fallback : null;
  if (!src) return base ? { fire: numberOr(base.fire, 0), water: numberOr(base.water, 0) } : null;
  return { fire: numberOr(src.fire, 0), water: numberOr(src.water, 0) };
}

function cloneSnapshotStatus(status, fallback = null) {
  const src = status && typeof status === 'object' ? status : {};
  const base = fallback && typeof fallback === 'object' ? fallback : {};
  return {
    gameId: typeof src.gameId === 'string'
      ? src.gameId
      : (typeof base.gameId === 'string' ? base.gameId : ''),
    roundNumber: numberOr(src.roundNumber, numberOr(base.roundNumber, 0)),
    running: src.running === undefined ? Boolean(base.running) : Boolean(src.running),
    waitingNext: src.waitingNext === undefined ? Boolean(base.waitingNext) : Boolean(src.waitingNext),
    gameOver: src.gameOver === undefined ? Boolean(base.gameOver) : Boolean(src.gameOver),
    paused: src.paused === undefined ? Boolean(base.paused) : Boolean(src.paused),
    roundWinnerId: numberOr(src.roundWinnerId, numberOr(base.roundWinnerId, 0)),
    matchWinnerId: numberOr(src.matchWinnerId, numberOr(base.matchWinnerId, 0)),
    matchTie: src.matchTie === undefined ? Boolean(base.matchTie) : Boolean(src.matchTie),
    nextRoundAt: numberOr(src.nextRoundAt, numberOr(base.nextRoundAt, 0)),
    matchRestartAt: numberOr(src.matchRestartAt, numberOr(base.matchRestartAt, 0)),
    roundEndsAt: numberOr(src.roundEndsAt, numberOr(base.roundEndsAt, 0)),
    roundStartFreezeUntilMs: numberOr(
      src.roundStartFreezeUntilMs,
      numberOr(base.roundStartFreezeUntilMs, 0)
    ),
    // What the match clock read when a match was stopped early; 0 for a match
    // that ran its clock out. This clone is a whitelist, so a field missing
    // from it never reaches the renderer at all.
    timeLeftAtEndMs: numberOr(src.timeLeftAtEndMs, numberOr(base.timeLeftAtEndMs, 0)),
    // Team mode. Absent outside it, which is why each field falls back to the
    // previous status rather than to a default: these ride the same partial
    // snapshots as everything else here.
    teamMode: src.teamMode === undefined ? Boolean(base.teamMode) : Boolean(src.teamMode),
    teamScores: cloneTeamScores(src.teamScores, base.teamScores),
    matchWinnerTeam: src.matchWinnerTeam === undefined
      ? (base.matchWinnerTeam ?? null)
      : (src.matchWinnerTeam || null)
  };
}

function cloneEffectState(effect) {
  if (!effect || typeof effect !== 'object') return null;
  return {
    type: typeof effect.type === 'string' ? effect.type : null,
    ticks: numberOr(effect.ticks, 0),
    stacks: numberOr(effect.stacks, 0)
  };
}

function toSnapshotStruct(state, fallbackState = null, options = {}) {
  const src = state && typeof state === 'object' ? state : {};
  const fallback = fallbackState && typeof fallbackState === 'object' ? fallbackState : {};
  const includeEvents = options.includeEvents === true;

  const snapshot = {
    serverNow: numberOr(src.serverNow, numberOr(fallback.serverNow, Date.now())),
    tick: numberOr(src.tick, numberOr(fallback.tick, 0)),
    target: numberOr(src.target, numberOr(fallback.target, 10)),
    scores: Array.isArray(src.scores) ? src.scores : (Array.isArray(fallback.scores) ? fallback.scores : []),
    roundScores: Array.isArray(src.roundScores)
      ? src.roundScores
      : (Array.isArray(fallback.roundScores) ? fallback.roundScores : []),
    players: Array.isArray(src.players) ? src.players : (Array.isArray(fallback.players) ? fallback.players : []),
    status: cloneSnapshotStatus(src.status, fallback.status),
    powerups: Array.isArray(src.powerups) ? src.powerups : (Array.isArray(fallback.powerups) ? fallback.powerups : []),
    standings: Array.isArray(src.standings) ? src.standings : (Array.isArray(fallback.standings) ? fallback.standings : [])
  };

  const sourcePowerupsVersion = src.powerupsVersion;
  const fallbackPowerupsVersion = fallback.powerupsVersion;
  if (
    sourcePowerupsVersion !== null
    && sourcePowerupsVersion !== undefined
    && Number.isFinite(Number(sourcePowerupsVersion))
  ) {
    snapshot.powerupsVersion = Number(sourcePowerupsVersion);
  } else if (
    !Array.isArray(src.powerups)
    && fallbackPowerupsVersion !== null
    && fallbackPowerupsVersion !== undefined
    && Number.isFinite(Number(fallbackPowerupsVersion))
  ) {
    snapshot.powerupsVersion = Number(fallbackPowerupsVersion);
  }

  if (includeEvents) {
    snapshot.deathEvents = Array.isArray(src.deathEvents)
      ? src.deathEvents
      : (Array.isArray(fallback.deathEvents) ? fallback.deathEvents : []);
  }

  return snapshot;
}

function cloneFrameState(state) {
  const src = state && typeof state === 'object' ? state : {};
  const next = {
    ...src,
    status: cloneSnapshotStatus(src.status),
    players: Array.isArray(src.players) ? src.players.map((player) => ({ ...player })) : []
  };
  return next;
}

function roundToTenths(value) {
  if (value === null || value === undefined) return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return Math.round(numeric * 10) / 10;
}

function setTextIfChanged(el, text) {
  if (!el) return;
  const next = String(text);
  if (el.textContent !== next) {
    el.textContent = next;
  }
}

export {
  clamp,
  lerp,
  easeOutQuad,
  easeOutBack,
  angleDiff,
  lerpAngle,
  numberOr,
  cloneSnapshotStatus,
  cloneEffectState,
  toSnapshotStruct,
  cloneFrameState,
  roundToTenths,
  setTextIfChanged
};
